import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Link } from 'react-router-dom';
import { 
  Star, Shield, Clock, Wrench, Zap, 
  Home as HomeIcon, Building, Phone, Mail, CheckCircle 
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import SEO from '@/components/ui-custom/SEO';

gsap.registerPlugin(ScrollTrigger);

const Home = () => {
  const heroRef = useRef<HTMLDivElement>(null);
  const plumbingRef = useRef<HTMLDivElement>(null);
  const electricalRef = useRef<HTMLDivElement>(null);
  const aboutRef = useRef<HTMLDivElement>(null);
  const servicesRef = useRef<HTMLDivElement>(null);
  const contactRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const heroTl = gsap.timeline({ delay: 0.1 });
      heroTl.fromTo('.hero-card', 
          { opacity: 0, scale: 0.96, y: 24 },
          { opacity: 1, scale: 1, y: 0, duration: 0.5, ease: 'power2.out' }
        );

      const sections = [
        { ref: plumbingRef, class: 'plumbing' },
        { ref: electricalRef, class: 'electrical' },
        { ref: aboutRef, class: 'about' },
        { ref: servicesRef, class: 'services' },
        { ref: contactRef, class: 'contact' },
      ];

      sections.forEach(({ ref, class: className }) => {
        if (!ref.current) return;
        gsap.fromTo(`.${className}-card`,
          { y: 100, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            duration: 0.5,
            ease: 'power2.out',
            scrollTrigger: {
              trigger: ref.current,
              start: 'top 85%',
              toggleActions: 'play none none reverse',
            },
          }
        );
      });
    });
    return () => ctx.revert();
  }, []);

  return (
    <>
      <SEO
        title="Ard's Plumbing & Electric | Dothan AL | 65+ Years Experience"
        description="Licensed plumber and electrician serving Dothan, Enterprise, Ozark, and the Wiregrass, AL. 65+ years experience. Mon-Fri 8AM-5PM. Call (334) 692-5537."
        keywords="plumber Dothan AL, electrician Dothan, plumbing and electrical services, Wiregrass plumber, Dothan AL home services"
        canonical="/"
      />

      {/* Hero Section */}
      <section ref={heroRef} className="min-h-screen bg-brand-blue relative flex items-center justify-center pt-20 pb-12 px-4">
        <div className="hero-card w-full max-w-6xl bg-brand-white rounded-[28px] overflow-hidden shadow-2xl">
          <div className="grid lg:grid-cols-2 min-h-[600px]">
            <div className="relative h-64 lg:h-auto">
              <img
                src="/images/hero-plumber.jpg"
                alt="Professional plumber providing service in Dothan AL"
                className="absolute inset-0 w-full h-full object-cover"
                fetchPriority="high" 
              />
              <div className="absolute top-4 right-4 bg-brand-navy text-white text-xs font-semibold px-3 py-1.5 rounded-full">
                Licensed & Insured
              </div>
            </div>

            <div className="p-8 lg:p-12 flex flex-col justify-center">
              <h1 className="hero-headline mb-6 font-heading font-black text-4xl lg:text-6xl text-brand-black leading-tight tracking-tight">
                <span>FAST FIXES.</span><br/>
                <span>LASTING WORK.</span><br/>
                <span className="text-brand-coral">REAL PEOPLE.</span>
              </h1>
              <p className="hero-subtext text-lg text-gray-700 mb-8 max-w-md">
                Plumbing & electrical in Dothan and the Wiregrass. Mon-Fri 8AM-5PM.
              </p>
              <div className="hero-cta flex flex-col sm:flex-row gap-4 mb-8">
                <Button asChild className="bg-brand-coral hover:bg-brand-coral/90 text-white rounded-full px-8 py-6 text-lg font-semibold shadow-md">
                  <Link to="/contact-us">Book Online</Link>
                </Button>
                <a href="tel:3346925537" className="inline-flex items-center justify-center gap-2 px-8 py-3 rounded-full border-2 border-brand-navy text-brand-navy font-semibold hover:bg-brand-navy hover:text-white transition-colors">
                  <Phone className="w-5 h-5" /> (334) 692-5537
                </a>
              </div>

              <div className="flex flex-wrap gap-4 pt-8 border-t border-gray-200">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Star className="w-4 h-4 text-brand-yellow fill-brand-yellow" /> 
                  <span>4-Star Rated</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Shield className="w-4 h-4 text-brand-blue" /> 
                  <span>Licensed & Insured</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Clock className="w-4 h-4 text-brand-coral" /> 
                  <span>Mon-Fri 8AM-5PM</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Plumbing Section */}
      <section ref={plumbingRef} className="min-h-screen bg-brand-blue relative flex items-center justify-center py-20 px-4">
        <div className="plumbing-card w-full max-w-6xl bg-brand-white rounded-[28px] overflow-hidden shadow-2xl">
          <div className="grid lg:grid-cols-2 min-h-[600px]">
            <div className="p-8 lg:p-12 flex flex-col justify-center order-2 lg:order-1">
              <h2 className="plumbing-headline mb-6 font-heading font-black text-3xl lg:text-5xl text-brand-black">
                PLUMBING <span className="text-brand-coral">THAT ACTUALLY SHOWS UP.</span>
              </h2>
              <ul className="space-y-4 mb-8">
                {['Leak repair & drain cleaning', 'Water heaters & fixtures', 'Sewer lines & repipes'].map((item) => (
                  <li key={item} className="flex items-center gap-3 text-gray-700">
                    <CheckCircle className="w-5 h-5 text-brand-blue" /> 
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <Button asChild className="bg-brand-coral hover:bg-brand-coral/90 text-white rounded-full px-8 py-6 text-lg font-semibold w-fit shadow-md">
                <Link to="/services/plumbing">Explore Plumbing Services</Link>
              </Button>
            </div>
            <div className="plumbing-image relative h-64 lg:h-auto order-1 lg:order-2">
              <img src="/images/plumbing-service.jpg" alt="Plumbing repair" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
            </div>
          </div>
        </div>
      </section>

      {/* Electrical Section */}
      <section ref={electricalRef} className="min-h-screen bg-brand-blue relative flex items-center justify-center py-20 px-4">
        <div className="electrical-card w-full max-w-6xl bg-brand-white rounded-[28px] overflow-hidden shadow-2xl">
          <div className="grid lg:grid-cols-2 min-h-[600px]">
            <div className="electrical-image relative h-64 lg:h-auto">
              <img src="/images/electrical-service.jpg" alt="Electrical work" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
            </div>
            <div className="p-8 lg:p-12 flex flex-col justify-center">
              <h2 className="electrical-headline mb-6 font-heading font-black text-3xl lg:text-5xl text-brand-black">
                ELECTRICAL DONE RIGHT <span className="text-brand-coral">THE FIRST TIME.</span>
              </h2>
              <ul className="space-y-4 mb-8">
                {['Panel upgrades & wiring', 'Lighting, outlets & switches', 'Troubleshooting & safety checks'].map((item) => (
                  <li key={item} className="flex items-center gap-3 text-gray-700">
                    <CheckCircle className="w-5 h-5 text-brand-blue" /> 
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <Button asChild className="bg-brand-coral hover:bg-brand-coral/90 text-white rounded-full px-8 py-6 text-lg font-semibold w-fit shadow-md">
                <Link to="/services/electrical">Explore Electrical Services</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-heading font-black text-3xl lg:text-4xl text-brand-black mb-4">
              SERVING THE WIREGRASS
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              From Dothan to Enterprise, Ozark to Geneva — we cover the entire Southeast Alabama Wiregrass region with fast, reliable plumbing and electrical service.
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 mb-8">
            {[
              { name: 'Dothan', path: '/service-areas/dothan-al' },
              { name: 'Enterprise', path: '/service-areas/enterprise-al' },
              { name: 'Ozark', path: '/service-areas/ozark-al' },
              { name: 'Geneva', path: '/service-areas/geneva-al' },
              { name: 'Headland', path: '/service-areas/headland-al' },
              { name: 'Midland City', path: '/service-areas/midland-city-al' },
              { name: 'Daleville', path: '/service-areas/daleville-al' },
              { name: 'Newton', path: '/service-areas/newton-al' },
              { name: 'Rehobeth', path: '/service-areas/rehobeth-al' },
            ].map(({ name, path }) => (
              <Link
                key={name}
                to={path}
                className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-center text-brand-blue font-semibold text-sm hover:bg-brand-blue hover:text-white hover:border-brand-blue transition-colors"
              >
                {name}, AL
              </Link>
            ))}
          </div>
          <div className="text-center">
            <Link to="/service-areas" className="text-brand-blue font-semibold hover:underline">
              View All Service Areas →
            </Link>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section ref={aboutRef} className="min-h-screen bg-brand-yellow relative flex items-center justify-center py-20 px-4">
        <div className="about-card w-full max-w-6xl bg-brand-white rounded-[28px] overflow-hidden shadow-2xl">
          <div className="grid lg:grid-cols-2 min-h-[600px]">
            <div className="p-8 lg:p-12 flex flex-col justify-center order-2 lg:order-1">
              <h2 className="about-headline mb-6 font-heading font-black text-3xl lg:text-5xl text-brand-black">
                LOCAL. LICENSED. <span className="text-brand-coral">STRAIGHT-FORWARD.</span>
              </h2>
              <div className="flex gap-4 mb-8">
                <span className="flex items-center gap-2 text-sm bg-brand-navy text-white px-4 py-2 rounded-full">
                  <Clock className="w-4 h-4" /> 65+ Years Experience
                </span>
                <span className="flex items-center gap-2 text-sm bg-brand-blue text-white px-4 py-2 rounded-full">
                  <Zap className="w-4 h-4" /> Mon-Fri 8AM-5PM
                </span>
              </div>
              <Button asChild className="bg-brand-coral hover:bg-brand-coral/90 text-white rounded-full px-8 py-6 text-lg font-semibold w-fit shadow-md">
                <Link to="/about">Our Story</Link>
              </Button>
            </div>
            <div className="about-image relative h-64 lg:h-auto order-1 lg:order-2">
              <img src="/images/team-van.jpg" alt="Service Van" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section ref={servicesRef} className="min-h-screen bg-brand-blue relative flex items-center justify-center py-20 px-4">
        <div className="services-card w-full max-w-6xl bg-brand-white rounded-[28px] overflow-hidden shadow-2xl">
          <div className="grid lg:grid-cols-2 min-h-[600px]">
            <div className="services-image relative h-64 lg:h-auto">
              <img src="/images/commercial-service.jpg" alt="Commercial services" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
            </div>
            <div className="p-8 lg:p-12 flex flex-col justify-center">
              <h2 className="services-headline mb-6 font-heading font-black text-3xl lg:text-5xl text-brand-black">
                HOMES & BUSINESSES. <span className="text-brand-coral">WE DO BOTH.</span>
              </h2>
              <div className="space-y-6 mb-8">
                <div className="flex items-start gap-4">
                  <div className="bg-brand-blue/10 p-2 rounded-full"><HomeIcon className="w-6 h-6 text-brand-blue" /></div>
                  <div><h4 className="font-bold">Residential</h4><p className="text-sm text-gray-600">Kitchens, baths, and more.</p></div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="bg-brand-coral/10 p-2 rounded-full"><Building className="w-6 h-6 text-brand-coral" /></div>
                  <div><h4 className="font-bold">Commercial</h4><p className="text-sm text-gray-600">Maintenance and code compliance.</p></div>
                </div>
              </div>
              <Button asChild className="bg-brand-coral hover:bg-brand-coral/90 text-white rounded-full px-8 py-6 text-lg font-semibold w-fit shadow-md">
                <Link to="/contact-us">Get a Free Estimate</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section ref={contactRef} className="min-h-screen bg-brand-navy relative flex items-center justify-center py-20 px-4">
        <div className="contact-card w-full max-w-6xl bg-brand-white rounded-[28px] overflow-hidden shadow-2xl">
          <div className="grid lg:grid-cols-2 min-h-[600px]">
            <div className="p-8 lg:p-12 flex flex-col justify-center order-2 lg:order-1">
              <h2 className="contact-headline mb-6 font-heading font-black text-3xl lg:text-5xl text-brand-black">
                SCHEDULE SERVICE <span className="text-brand-coral">TODAY.</span>
              </h2>
              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                  <div className="bg-brand-blue p-3 rounded-full"><Phone className="text-white w-6 h-6" /></div>
                  <div><p className="text-xs text-gray-500">Call Us</p><p className="font-bold">(334) 692-5537</p></div>
                </div>
                <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                  <div className="bg-brand-coral p-3 rounded-full"><Wrench className="text-white w-6 h-6" /></div>
                  <div><p className="text-xs text-gray-500">Book Online</p><p className="font-bold">24/7 Scheduling</p></div>
                </div>
                <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                  <div className="bg-brand-navy p-3 rounded-full"><Mail className="text-white w-6 h-6" /></div>
                  <div><p className="text-xs text-gray-500">Email Us</p><p className="font-bold">support@ardsdothan.com</p></div>
                </div>
              </div>
              <p className="text-sm text-gray-500 flex items-center gap-2">
                <Zap className="w-4 h-4 text-brand-yellow fill-brand-yellow" /> Emergency? We prioritize same-day calls.
              </p>
            </div>
            <div className="contact-image relative h-64 lg:h-auto order-1 lg:order-2">
              <img src="/images/support-team.jpg" alt="Support Team" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;